/**
 * TemplateLibraryDialog — browse and install pre-built workflow templates.
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Library, Loader2, Check, Globe, Trash2 } from 'lucide-react';
import WorkflowDiagram from '@/components/dms/WorkflowDiagram';
import api from '@/lib/api';

export default function TemplateLibraryDialog({ projectId, open, onClose, onInstalled }) {
  const [inner, setInner] = useState('standard');
  const [lib, setLib] = useState([]);
  const [market, setMarket] = useState([]);
  const [checked, setChecked] = useState({});
  const [marketChecked, setMarketChecked] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const [r1, r2] = await Promise.all([
        api.get(`/dms/workflows/library`),
        api.get(`/dms/marketplace/templates`),
      ]);
      setLib(r1.data.templates || []);
      setMarket(r2.data || []);
      const all = {};
      (r1.data.templates || []).forEach(t => { all[t.name] = true; });
      setChecked(all);
    })();
  }, [open]);

  const installStandard = async () => {
    const names = Object.entries(checked).filter(([_, v]) => v).map(([k]) => k);
    if (names.length === 0) { toast.error('Select at least one template'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/dms/projects/${projectId}/workflows/library/install`, { names });
      toast.success(`Installed ${r.data.installed}, skipped ${r.data.skipped_existing} existing`);
      onInstalled?.(); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Install failed'); }
    finally { setBusy(false); }
  };

  const installMarket = async () => {
    const ids = Object.entries(marketChecked).filter(([_, v]) => v).map(([k]) => k);
    if (ids.length === 0) { toast.error('Select at least one entry'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/dms/projects/${projectId}/marketplace/install`, { marketplace_ids: ids });
      toast.success(`Installed ${r.data.installed}, skipped ${r.data.skipped_existing} existing`);
      onInstalled?.(); onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Install failed'); }
    finally { setBusy(false); }
  };

  const unpublish = async (mid) => {
    if (!window.confirm('Unpublish this marketplace entry?')) return;
    try {
      await api.delete(`/dms/marketplace/templates/${mid}`);
      toast.success('Unpublished');
      const r = await api.get(`/dms/marketplace/templates`);
      setMarket(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Unpublish failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Library className="w-4 h-4 text-fuchsia-500" /> Workflow Templates — Library &amp; Marketplace</DialogTitle>
        </DialogHeader>
        <Tabs value={inner} onValueChange={setInner}>
          <TabsList className="bg-slate-100 dark:bg-slate-800 p-0.5">
            <TabsTrigger value="standard"    className="text-xs" data-testid="lib-tab-standard"><Library className="w-3.5 h-3.5 mr-1" /> Standard Library</TabsTrigger>
            <TabsTrigger value="marketplace" className="text-xs" data-testid="lib-tab-marketplace"><Globe className="w-3.5 h-3.5 mr-1" /> Marketplace ({market.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="standard" className="mt-3">
            <div className="grid gap-3">
              {lib.length === 0 ? <div className="text-xs text-slate-500 p-4">Loading library…</div> : lib.map(t => (
                <label key={t.name} className="cursor-pointer">
                  <div className={`border-2 rounded-lg p-3 transition ${checked[t.name] ? 'border-sky-500 bg-sky-50 dark:bg-sky-500/10' : 'border-slate-200 dark:border-slate-700/40 bg-slate-50 dark:bg-slate-800/40'}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <input type="checkbox" checked={!!checked[t.name]} onChange={(e) => setChecked({ ...checked, [t.name]: e.target.checked })} data-testid={`lib-check-${t.name}`} />
                      <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">{t.name}</span>
                      <span className="text-[9px] px-2 py-0.5 rounded-full bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-300 uppercase">{t.entity_type}</span>
                      <span className="text-[10px] text-slate-500">— {t.description}</span>
                    </div>
                    <WorkflowDiagram stages={t.stages} compact />
                  </div>
                </label>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="marketplace" className="mt-3">
            {market.length === 0 ? (
              <div className="text-xs text-slate-500 p-6 text-center border border-dashed border-slate-300 dark:border-slate-700 rounded-lg">
                Nothing published yet. Open any template in your Templates list → click <b>Publish to marketplace</b> to share it with other projects.
              </div>
            ) : (
              <div className="grid gap-3">
                {market.map(m => (
                  <label key={m.id} className="cursor-pointer">
                    <div className={`border-2 rounded-lg p-3 transition ${marketChecked[m.id] ? 'border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-500/10' : 'border-slate-200 dark:border-slate-700/40 bg-slate-50 dark:bg-slate-800/40'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <input type="checkbox" checked={!!marketChecked[m.id]} onChange={(e) => setMarketChecked({ ...marketChecked, [m.id]: e.target.checked })} data-testid={`market-check-${m.name}`} />
                        <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">{m.name}</span>
                        <span className="text-[9px] px-2 py-0.5 rounded-full bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-500/20 dark:text-fuchsia-300 uppercase">{m.entity_type}</span>
                        <span className="text-[10px] text-slate-500 flex-1 truncate">— {m.description}</span>
                        <span className="text-[10px] text-slate-500">{m.install_count} install{m.install_count === 1 ? '' : 's'}</span>
                        <button onClick={(e) => { e.preventDefault(); unpublish(m.id); }} className="text-red-500 hover:text-red-700 opacity-50 hover:opacity-100" title="Unpublish (publisher only)"><Trash2 className="w-3 h-3" /></button>
                      </div>
                      <div className="text-[10px] text-slate-500 mb-1">Published by <span className="font-mono">{m.published_by}</span> · from <span className="font-mono">{m.published_from}</span></div>
                      <WorkflowDiagram stages={m.stages} compact />
                    </div>
                  </label>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {inner === 'standard' ? (
            <Button className="bg-emerald-600 text-white" onClick={installStandard} disabled={busy} data-testid="lib-install">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Install Selected
            </Button>
          ) : (
            <Button className="bg-fuchsia-600 text-white" onClick={installMarket} disabled={busy} data-testid="market-install">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              Install from Marketplace
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
